<template>
  <main class="main colors">
    <div class="f container">
      <LazyUiBreadCrumbsV2
        :items="[
          {
            title: t('colors'),
            link: localePath('/colors'),
          }, {
            title: route.params.id ? t(`color_translates.${(route.params.id as string).replace('-', '_')}`) : 'Color page',
            link: localePath(`/colors/${route.params.id}`),
          },
        ]"
      />
      <button
        :class="{ compressed }"
        aria-label="compressed button"
        class="compress-btn ht hd"
        @click="toggleCompressed"
      >
        <svg>
          <use
            v-if="compressed"
            xlink:href="/icons/sprite.svg#grid-1"
          />
          <use
            v-else
            xlink:href="/icons/sprite.svg#grid-2"
          />
        </svg>
      </button>
    </div>
    <PagesColors
      :is-loading="status === 'pending'"
      :items="items"
      :local="pageText"
      :page-info="((data || {}) as DynamicPageInfo).info as DynamicPageFullInfo"
      :pages-length="data?.pagesLength || 0"
    />
  </main>
</template>

<script lang="ts" setup>
import type {
  AdvancedSearchBody,
  DynamicPageFullInfo,
  DynamicPageInfo,
  DynamicPageLocalType,
} from '~/graphql/generated/schema-types';
import { useAlternateLinks } from '~/composables/alternate-links';
import { EventBus } from '~/composables/bus';
import { useDefineProductWithAdvSearchItem } from '~/composables/define-product';
import { getDynamicPage } from '~/utils/api';

const { locale, t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { compressed, toggleCompressed } = useCompressed();

const page = computed(() => {
  return Number(route.query.page) || 1;
});

async function getItems() {
  try {
    const {
      data,
      errors,
    } = await getDynamicPage(`colors/${route.params.id as string}`, locale.value, page.value);

    if (errors?.length) {
      throw new Error(errors[0].message);
    }

    return {
      items: data.dynamicPage.data.result as AdvancedSearchBody[],
      info: data.dynamicPage.info as DynamicPageFullInfo,
      pagesLength: data.dynamicPage.data.max_pages || 0,
    };
  } catch (e: any) {
    if (e.name !== 'AbortError') {
      EventBus.$emit('notify', {
        success: false,
        msg: e.message || 'An error occurred',
      });
    }

    console.log('getWeaponItems() error', e.message);
    throw new Error(e.message || 'An error occurred');
    // return {
    //   items: [] as AdvancedSearchBody[],
    //   info: {} as DynamicPageFullInfo,
    //   pagesLength: 0,
    // }
  }
}

const { data, error, status } = await useAsyncData(`color-items${route.params.id}`, getItems, {
  watch: [page],
});

if (error.value) {
  console.error('Error fetching color items', error.value);
  throw createError({
    status: 404,
    statusText: `Error fetching color items: ${error.value.message}`,
  });
}

const items = computed(() => data.value?.items || [] as AdvancedSearchBody[]);
const pageText = computed(() => data.value?.info?.locals?.find(i => i.local === locale.value) || {} as DynamicPageLocalType);

watch(page, () => {
  if (process.client) {
    window.scrollTo(0, 0);
  }
});

const title = computed(() => {
  return pageText.value?.seo_tags?.title?.replace('Title -', '')?.trim() || 'Title colors page';
});

const description = computed(() => {
  return pageText.value?.seo_tags?.description?.replace('{color}', t(`color_translates.${(route.params.id as string).replace('-', '_')}`)) || 'Colors page description';
});

const weaponIcon = computed(() => {
  return getImageFromWaxpeer(items.value[0]?.market_hash_name || '');
});

const { alternateLinks, canonical } = useAlternateLinks(`/colors/${route.params.id}`);

useHead({
  title: () => title.value,
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => title.value,
  ogTitle: () => title.value,
  description: () => description.value,
  ogDescription: () => description.value,
  ogImage: () => weaponIcon.value,
  twitterCard: 'summary_large_image',
  twitterTitle: () => title.value,
  twitterDescription: () => description.value,
  twitterImage: () => weaponIcon.value,
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

const { defineUseProduct } = useDefineProductWithAdvSearchItem();

useSchemaOrg([
  defineItemList({
    id: `itemlist-${title.value.replace(/\s+/g, '-').toLowerCase()}-weapon-page-${locale.value}`, // Уникальный ID для списка
    name: title.value,
    description: description.value,
    image: () => weaponIcon.value,
    itemListElement: await Promise.all(items.value.map(async (weapon, index) => {
      const product = await defineUseProduct(weapon);

      return {
        '@type': 'ListItem',
        'position': index + 1,
        'item': product,
        'id': product.id || `item-${index + 1}-${weapon?.market_hash_name}`,
      };
    })),
  }),
  defineBreadcrumb({
    itemListElement: [
      { name: () => t('main'), item: '/' },
      { name: title.value, item: localePath(`/colors/${route.params.id}`) },
    ],
  }),
]);
</script>

<style lang="scss" scoped>
.colors {
  padding: 15rem 2rem 4rem;
  margin: 0 auto;
  width: 100%;

  .compress-btn {
    width: 40px;
    height: 40px;
    padding: 5px;
    border: 1px solid var(--gray);

    &.compressed {
      padding: 8px;
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }

  @media screen and (max-width: 1115px) {
    padding-top: 9rem;
  }
  @media screen and (max-width: 979px) {
    padding-top: 7rem;
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
